import ChatRequestVariablesHint from '@components/ChatRequestVariablesHint'
import { Localizable } from '@components/localizable/Localizable'
import { TextEditorVariable } from '@components/tiptap/tiptap-ui/variables-dropdown/variables-dropdown'
import SBPhoneInput from '@modules/sb-antd-phone-input/SBPhoneInput'
import { useAppSelector } from '@store/hooks'
import { selectSelectedProgram } from '@store/membership/ProgramReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBRM, SBRMFormActionProps } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { gtmContainerIdRule, isE164Phone, requiredRule } from '@utils/rules'
import { Alert, ColorPicker, Form, Input, Radio, Space } from 'antd'
import { useEffect } from 'react'

const Update = ({ form, formProps }: SBRMFormActionProps) => {
  const item = useAppSelector(selectSelectedProgram())

  const programProtection = Form.useWatch(['protection'], form)
  const programChatType = Form.useWatch(['chatType'], form)

  useEffect(() => {
    if (!item) return
    form.setFieldsValue({
      ...item,
      protection: item.passcode ? 'protected' : 'public',
      passcode: item.passcode || '',
      chatType: item.chatType ?? 'intercom',
      whatsappNumber: {
        typed: item?.whatsappNumber,
        raw: item?.whatsappNumber,
      },
    })
  }, [item])

  useEffect(() => {
    if (programProtection === 'public') {
      form.setFieldValue('passcode', '')
    }
  }, [programProtection])

  if (item === undefined) {
    return <></>
  }

  return (
    <Form {...formProps} form={form} layout="vertical">
      <Form.Item name="id" hidden style={{ display: 'none' }} rules={[]}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="slug" hidden style={{ display: 'none' }} rules={[]}>
        <Input type="hidden" />
      </Form.Item>

      <SBRM.FormSection section="programName">
        <Localizable.Input
          label={'Name'}
          name={'name'}
          rules={[requiredRule()]}
          max={50}
        />
      </SBRM.FormSection>

      <SBRM.FormSection section="whatsapp">
        <Form.Item label={'Channel'} name={'chatType'}>
          <Radio.Group>
            <Radio.Button value="intercom">Intercom</Radio.Button>
            <Radio.Button value="whatsapp">WhatsApp</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Alert
          style={{ marginBottom: 16 }}
          title="This channel is used by the concierge tab and by any event whose booking method is “Chat request”."
          type="info"
        />

        <Form.Item
          label="Phone"
          name={'whatsappNumber'}
          rules={
            programChatType === 'whatsapp'
              ? [requiredRule(), isE164Phone()]
              : []
          }
        >
          <SBPhoneInput
            enableSearch
            disableParentheses
            sbInitialValue={item.whatsappNumber}
          />
        </Form.Item>

        {programChatType === 'whatsapp' && (
          <>
            <Alert
              style={{ marginBottom: 16 }}
              title="These details fill the contact card a member can save to their phone from the app concierge screen (the “Save in my contacts” button, shown only for the WhatsApp channel)."
              type="info"
            />
            <Localizable.Input
              label={'Contact first name'}
              name={'conciergeContactFirstName'}
              rules={[requiredRule()]}
              max={50}
            />
            <Localizable.Input
              label={'Contact last name'}
              name={'conciergeContactLastName'}
              rules={[requiredRule()]}
              max={50}
            />
            <Localizable.Input
              label={'Contact company'}
              name={'conciergeContactCompany'}
              rules={[requiredRule()]}
              max={80}
            />
          </>
        )}

        <Localizable.TextArea
          label={'Event chat request message'}
          name={'eventChatRequestMessage'}
          rules={[]}
          max={500}
        />
        <ChatRequestVariablesHint />
      </SBRM.FormSection>

      <SBRM.FormSection section="loyaltyTier">
        <Form.Item label={'Tag'} name="loyaltyTier">
          <SBAsyncSelect
            entity={Entity.loyaltyTiers}
            style={{ width: '100%' }}
            allowClear
          />
        </Form.Item>
      </SBRM.FormSection>

      <SBRM.FormSection section="currency">
        <Form.Item
          label={'Currency'}
          name="currency"
          rules={[requiredRule()]}
          noStyle
        >
          <SBAsyncSelect entity={Entity.currency} style={{ width: '100%' }} />
        </Form.Item>
      </SBRM.FormSection>

      <SBRM.FormSection section="paymentIntegration">
        <Form.Item
          label={'Merchant account'}
          name="paymentIntegration"
          rules={[requiredRule()]}
        >
          <SBAsyncSelect
            entity={Entity.adyenConfig}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </SBRM.FormSection>

      <SBRM.FormSection section="protection">
        <Form.Item label={'Protection'} name={'protection'}>
          <Radio.Group>
            <Radio.Button value="public">Public</Radio.Button>
            <Radio.Button value="protected">Protected</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Passcode"
          name="passcode"
          rules={programProtection === 'protected' ? [requiredRule()] : []}
          hidden={programProtection !== 'protected'}
        >
          <Input />
        </Form.Item>
      </SBRM.FormSection>

      <SBRM.FormSection section="seo">
        <Localizable.Input
          label={'Meta title'}
          name={'metaTitle'}
          rules={[requiredRule()]}
          max={80}
        />
        <Localizable.TextArea
          label={'Meta description'}
          name={'metaDescription'}
          rules={[requiredRule()]}
          max={255}
        />
      </SBRM.FormSection>

      <SBRM.FormSection section="color">
        <Form.Item
          name="primaryColor"
          getValueFromEvent={(color) => color.toHexString()}
          noStyle
        >
          <ColorPicker showText />
        </Form.Item>
      </SBRM.FormSection>

      <SBRM.FormSection section="success">
        <Localizable.Input
          label={'Review Title'}
          name={'toReviewTitle'}
          rules={[requiredRule()]}
          max={80}
        />
        <Localizable.TextEditor
          label={'Review Body'}
          name="toReviewBody"
          rules={[requiredRule()]}
        />
      </SBRM.FormSection>

      <SBRM.FormSection section="emailSubscriptionAccepted">
        <Localizable.Input
          label={'Subject'}
          name={'emailSubscriptionAcceptedTitle'}
          rules={[requiredRule()]}
          max={80}
        />
        <Localizable.TextEditor
          label={'Content'}
          name="emailSubscriptionAcceptedBody"
          rules={[requiredRule()]}
          variables={[
            TextEditorVariable.FIRST_NAME,
            TextEditorVariable.LAST_NAME,
            TextEditorVariable.PLAN_NAME,
            TextEditorVariable.CODE,
          ]}
        />
      </SBRM.FormSection>

      <SBRM.FormSection section="emailSubscriptionPaymentFailed">
        <Localizable.Input
          label={'Title'}
          name={'emailSubscriptionPaymentFailedTitle'}
          rules={[requiredRule()]}
          max={80}
        />
        <Localizable.TextEditor
          label={'Content'}
          name="emailSubscriptionPaymentFailedBody"
          rules={[requiredRule()]}
          variables={[
            TextEditorVariable.FIRST_NAME,
            TextEditorVariable.LAST_NAME,
            TextEditorVariable.PLAN_NAME,
            TextEditorVariable.SUBSCRIPTION_MANAGE_LINK,
          ]}
        />
      </SBRM.FormSection>

      <SBRM.FormSection section="emailSubscriptionReceived">
        <Localizable.Input
          label={'Title'}
          name={'emailSubscriptionReceivedTitle'}
          rules={[requiredRule()]}
          max={80}
        />
        <Localizable.TextEditor
          label={'Content'}
          name="emailSubscriptionReceivedBody"
          rules={[requiredRule()]}
          variables={[
            TextEditorVariable.FIRST_NAME,
            TextEditorVariable.LAST_NAME,
            TextEditorVariable.PLAN_NAME,
          ]}
        />
      </SBRM.FormSection>

      <SBRM.FormSection section="emailSubscriptionRejected">
        <Localizable.Input
          label={'Title'}
          name={'emailSubscriptionRejectedTitle'}
          rules={[requiredRule()]}
          max={80}
        />
        <Localizable.TextEditor
          label={'Content'}
          name="emailSubscriptionRejectedBody"
          rules={[requiredRule()]}
          variables={[
            TextEditorVariable.FIRST_NAME,
            TextEditorVariable.LAST_NAME,
            TextEditorVariable.PLAN_NAME,
            TextEditorVariable.REASON,
          ]}
        />
      </SBRM.FormSection>

      <SBRM.FormSection section="emailRenewalFailed">
        <Localizable.Input
          label={'Title'}
          name={'emailSubscriptionFailedToRenewTitle'}
          rules={[requiredRule()]}
          max={80}
        />
        <Localizable.TextEditor
          label={'Content'}
          name="emailSubscriptionFailedToRenewBody"
          rules={[requiredRule()]}
          variables={[
            TextEditorVariable.FIRST_NAME,
            TextEditorVariable.LAST_NAME,
            TextEditorVariable.PLAN_NAME,
            TextEditorVariable.PLAN_PRICE,
            TextEditorVariable.RENEWAL_DATE,
            TextEditorVariable.SUBSCRIPTION_MANAGE_LINK,
          ]}
        />
      </SBRM.FormSection>

      <SBRM.FormSection section="bankTransfer">
        <Space orientation="vertical" size={20} className="w-full">
          <Form.Item
            label={'IBAN'}
            name="bankTransferIBAN"
            rules={[requiredRule()]}
          >
            <Input className="w-full" />
          </Form.Item>
          <Localizable.TextEditor
            label={'Bank transfer description'}
            name="bankTransferText"
            rules={[requiredRule()]}
          />
        </Space>
      </SBRM.FormSection>

      <SBRM.FormSection section="gtm">
        <Form.Item
          label={'GTM Container ID'}
          name="gtmContainerId"
          rules={[gtmContainerIdRule()]}
        >
          <Input />
        </Form.Item>
      </SBRM.FormSection>

      <SBRM.FormSection section="concierge">
        <Localizable.Input
          label={'Concierge name'}
          name={'conciergeName'}
          rules={[requiredRule()]}
          max={50}
        />
        <Localizable.Input
          label={'Availability'}
          name={'conciergeAvailability'}
          rules={[requiredRule()]}
          max={80}
        />
        <Localizable.Input
          label={'Opening hours'}
          name={'conciergeHours'}
          rules={[requiredRule()]}
          max={80}
        />
      </SBRM.FormSection>

      <SBRM.FormSection section="complementaryItems">
        <Localizable.Input
          label={'Name'}
          name={'name'}
          rules={[requiredRule()]}
        />
      </SBRM.FormSection>
    </Form>
  )
}

export default Update
